// onboarding.ts — G11 teach-by-doing first-run tutorial.
//
// A skippable, <30s, in-context tutorial that teaches the five verbs ON a real-looking (scripted)
// easy board, one concept per beat, with minimal text. It is NOT a Sim — it is a fully scripted
// overlay rendered into whatever scene hosts it (the MenuScene), so it is deterministic, never
// touches Core, and composes with the existing menu overlay/teardown without registering a new
// scene. The "seen" flag persists in localStorage so a returning player is never forced into it
// again; a "How to play" control (Menu + Pause) replays it on demand via openOnboarding(force).
//
// Beat ladder (one concept each, tap / Enter / SPACE to advance, SKIP always available):
//   0 WELCOME  — the rule banner: "Eat only FRUIT". Read the rule first.
//   1 MOVE     — arrows / swipe; movement only moves.
//   2 JUDGE    — read the cell you're standing on (does it fit the rule?).
//   3 CHOMP    — deliberate "eat here" for the current tile.
//   4 SKIP     — a WRONG cell is safe to stand on; move off it to LOCK it shut.
//   5 SCAN     — reveal nearby true answers when unsure (limited charges).
//   6 GAUNTLET — thread a row of decoys onto a correct cell for a ×bonus.
//   7 DONE     — "You've got it!" → start playing.
//
// Pure Phaser + the shared palette/font/juice/audio. No Core, no network, no determinism touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { audio } from './audio';
import { track } from '../analytics/client';
import { motionAllowed, haptic } from './settings';
import { typePx } from './ui/typeScale';
import { glowPulse, particleBurst, floatText, drawDashedRect, vignettePulse } from './juice';
import { drawIcon } from './icons';
import { pushInputBlocker, popInputBlocker } from './ui';

const SEEN_KEY = 'trivia.onboarding.seen.v1';

/** Has the player completed (or skipped) the tutorial before? Tolerant of blocked storage. */
export function onboardingSeen(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist the "seen" flag so the tutorial is never auto-forced again. Best-effort. */
export function markOnboardingSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // private mode / blocked storage — the flag lives for the session only; that's acceptable.
  }
}

// A tiny scripted board: 3 cols x 3 rows of fruit vs not-food (hook coherence with the fruit
// training board — the muncher's fantasy is EATING, so the first rule ever read is about food).
//   0:apple(✓)  1:rock(✗)   2:pear(✓)
//   3:mango(✓)  4:sock(✗)   5:fig(✓)
//   6:chair(✗)  7:soap(✗)   8:plum(✓)  <- the GAUNTLET lane: pass chair/soap, then eat plum
// fruit = correct (fits the rule), everything else = decoy. The muncher starts at cell 3 (mango).
type Kind = 'correct' | 'decoy';
interface MiniCell {
  text: string;
  kind: Kind;
}
const COLS = 3;
const ROWS = 3;
const MINI: MiniCell[] = [
  { text: 'apple', kind: 'correct' },
  { text: 'rock', kind: 'decoy' },
  { text: 'pear', kind: 'correct' },
  { text: 'mango', kind: 'correct' },
  { text: 'sock', kind: 'decoy' },
  { text: 'fig', kind: 'correct' },
  { text: 'chair', kind: 'decoy' },
  { text: 'soap', kind: 'decoy' },
  { text: 'plum', kind: 'correct' },
];
const START_POS = 3;

interface CellGfx {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  glyph: Phaser.GameObjects.Text;
  lockBorder: Phaser.GameObjects.Graphics;
  eaten: boolean;
  armed: boolean;
  revealed: boolean;
}

interface Beat {
  /** short headline (verb) */
  title: string;
  /** one-line minimal body */
  body: string;
  /** scripted demo to run when the beat opens (advances itself or waits for tap) */
  run: () => void;
}

/** Module guard: at most one tutorial overlay alive at a time (prevents stacked instances/handlers). */
let activeOnboarding = false;

/**
 * Open the onboarding overlay on `scene`. Always opens (the "How to play" reopen replays it on
 * demand); first-run auto-launch is gated by the caller on onboardingSeen(). Calls `onDone` exactly
 * once when the player finishes or skips (the host uses this to clear its "tutorial active" guard).
 * No-ops if a tutorial is already open (so repeated taps on a "How to play" button can't stack).
 */
export function openOnboarding(scene: Phaser.Scene, onDone: () => void): void {
  if (activeOnboarding) return;
  activeOnboarding = true;
  // Block the menu's DOM topic <input> for the tutorial's lifetime (the opaque canvas veil can't
  // cover a DOM element above the canvas).
  pushInputBlocker();
  new Onboarding(scene, () => {
    activeOnboarding = false;
    popInputBlocker();
    onDone();
  }).start();
}

class Onboarding {
  private scene: Phaser.Scene;
  private onDone: () => void;
  private done = false;
  /** ensures onDone fires exactly once across both the finish-fade and scene-shutdown paths. */
  private notified = false;

  private layer!: Phaser.GameObjects.Container;
  private cells: CellGfx[] = [];
  private muncher!: Phaser.GameObjects.Container;

  private gridX = 0;
  private gridY = 0;
  private cell = 0;
  private pos = START_POS;

  // text card
  private titleText!: Phaser.GameObjects.Text;
  private bodyText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private ctaBg!: Phaser.GameObjects.Rectangle;
  private progressDots: Phaser.GameObjects.Arc[] = [];

  private beats: Beat[] = [];
  private beatIndex = 0;
  /** true while a scripted demo animation is playing — taps are ignored until it finishes. */
  private busy = false;
  /** input listeners we add to the scene and must remove on teardown (so the menu isn't hijacked). */
  private keyHandler?: (ev: KeyboardEvent) => void;

  constructor(scene: Phaser.Scene, onDone: () => void) {
    this.scene = scene;
    this.onDone = onDone;
  }

  // --- lifecycle ------------------------------------------------------------
  start(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    audio().unlock();
    audio().playSfx('uiConfirm', { volume: 0.7 });
    track('tutorial_started');

    // Depth above the shared modal layer (ui.ts modals sit at 30_000) so "How to play" from the
    // in-game Pause menu draws cleanly on top of the paused modal.
    this.layer = this.scene.add.container(0, 0).setDepth(31_000);
    // Opaque dark veil: a clean, focused tutorial surface (no menu/board bleed-through) that also
    // swallows taps to whatever is beneath (its own pointer handler advances beats).
    const veil = this.scene.add
      .rectangle(W / 2, H / 2, W, H, PALETTE.bg, 1)
      .setInteractive({ useHandCursor: true });
    veil.on('pointerup', () => this.onTap());
    this.layer.add(veil);

    this.buildHeader(W, H);
    this.buildBoard(W, H);
    this.buildMuncher();
    this.buildTextCard(W, H);
    this.buildSkipButton(W, H);
    this.buildBeats();

    // Keyboard: SPACE / ENTER / RIGHT advance; ESC skips. Registered on the scene and torn down so
    // the MenuScene's own key handlers aren't permanently shadowed.
    this.keyHandler = (ev: KeyboardEvent): void => {
      // Capture-phase + stopPropagation so these keys drive ONLY the tutorial and never leak to the
      // host scene's handlers (e.g. the GameScene ESC/P pause toggle when replayed from Pause).
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        this.finish(true);
        return;
      }
      if (ev.key === ' ' || ev.key === 'Enter' || ev.key === 'ArrowRight') {
        ev.preventDefault();
        ev.stopPropagation();
        this.onTap();
      }
    };
    window.addEventListener('keydown', this.keyHandler, { capture: true });

    // entrance
    this.layer.setAlpha(0);
    this.scene.tweens.add({ targets: this.layer, alpha: 1, duration: 220, ease: 'Quad.easeOut' });

    // Clean up if the host scene leaves under us (e.g. a deep-link auto-launch).
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.showBeat(0);
  }

  /** Remove input listeners + destroy the overlay, and fire onDone exactly once (idempotent). */
  private teardown(): void {
    if (this.keyHandler) {
      window.removeEventListener('keydown', this.keyHandler, { capture: true });
      this.keyHandler = undefined;
    }
    this.layer?.destroy();
    this.layer = undefined as unknown as Phaser.GameObjects.Container;
    if (!this.notified) {
      this.notified = true;
      this.onDone();
    }
  }

  /** End the tutorial: persist seen, fade out, then tear down (releasing the module guard). */
  private finish(skipped: boolean): void {
    if (this.done) return;
    this.done = true;
    markOnboardingSeen();
    audio().playSfx(skipped ? 'uiTap' : 'uiConfirm');
    if (!this.layer) {
      this.teardown();
      return;
    }
    this.scene.tweens.add({
      targets: this.layer,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeIn',
      onComplete: () => this.teardown(),
    });
  }

  // --- layout / build -------------------------------------------------------
  private buildHeader(W: number, H: number): void {
    // Rule banner — a CENTERED rounded pill (matches the reference), "RULE" cyan + rule text white.
    const bannerH = Math.min(56, H * 0.075);
    const fs = `${typePx(this.scene, 'body', { vw: 0.034, min: 14, max: 20 })}px`;
    const ruleKw = this.scene.add
      .text(0, H * 0.07, 'RULE', { fontFamily: FONT.display, fontSize: fs, color: hex(PALETTE.scan), fontStyle: 'bold' })
      .setOrigin(0, 0.5);
    const ruleTxt = this.scene.add
      .text(0, H * 0.07, 'Eat only FRUIT', { fontFamily: FONT.family, fontSize: fs, color: hex(PALETTE.text), fontStyle: 'bold' })
      .setOrigin(0, 0.5);
    const contentW = ruleKw.width + 10 + ruleTxt.width;
    const pillW = Math.min(W * 0.92, contentW + 56);
    const pill = this.scene.add.graphics();
    pill.fillStyle(PALETTE.banner, 1);
    pill.fillRoundedRect(W / 2 - pillW / 2, H * 0.07 - bannerH / 2, pillW, bannerH, bannerH / 2);
    pill.lineStyle(2, PALETTE.panelBorder, 1);
    pill.strokeRoundedRect(W / 2 - pillW / 2, H * 0.07 - bannerH / 2, pillW, bannerH, bannerH / 2);
    const startX = W / 2 - contentW / 2;
    ruleKw.setX(startX);
    ruleTxt.setX(startX + ruleKw.width + 10);
    this.layer.add([pill, ruleKw, ruleTxt]);

    const tag = this.scene.add
      .text(W / 2, H * 0.07 + bannerH / 2 + 12, 'QUICK TUTORIAL', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this.scene, 'caption', { vw: 0.02 })}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.layer.add(tag);
  }

  private buildBoard(W: number, H: number): void {
    // A compact, centered 3x3 board sitting in the middle band of the screen.
    const boardTop = H * 0.16;
    const boardBottom = H * 0.62;
    const maxByH = (boardBottom - boardTop) / ROWS;
    const maxByW = (W * 0.74) / COLS;
    this.cell = Math.floor(Math.min(maxByH, maxByW));
    const boardW = this.cell * COLS;
    const boardH = this.cell * ROWS;
    this.gridX = (W - boardW) / 2;
    this.gridY = boardTop + (boardBottom - boardTop - boardH) / 2;

    for (let id = 0; id < MINI.length; id++) {
      const m = MINI[id];
      const { x, y } = this.cellCenter(id);
      const w = this.cell - 6;
      const h = this.cell - 6;
      const container = this.scene.add.container(x, y);
      const bg = this.scene.add.rectangle(0, 0, w, h, PALETTE.tile, 1).setStrokeStyle(2, PALETTE.tileBorder);
      const label = this.scene.add
        .text(0, 0, m.text, {
          fontFamily: FONT.family,
          fontSize: `${Math.max(16, Math.floor(w * 0.42))}px`,
          color: hex(PALETTE.text),
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      // S3 item 27: measured shrink-to-fit with ~10% inner padding (mirrors the live board's fitter),
      // so a wide word like "apple" never edge-kisses the tile.
      {
        const boxW = w * 0.9;
        let lfs = Math.max(16, Math.floor(w * 0.42));
        while (label.width > boxW && lfs > 9) {
          lfs -= 1;
          label.setFontSize(lfs);
        }
      }
      const glyph = this.scene.add
        .text(w / 2 - 3, -h / 2 + 2, '', {
          fontFamily: FONT.family,
          fontSize: `${Math.max(12, Math.floor(w * 0.32))}px`,
          color: hex(PALETTE.text),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0)
        .setVisible(false);
      const lockBorder = this.scene.add.graphics().setVisible(false);
      container.add([bg, lockBorder, label, glyph]);
      this.layer.add(container);
      this.cells.push({ container, bg, label, glyph, lockBorder, eaten: false, armed: false, revealed: false });
    }
  }

  private buildMuncher(): void {
    const r = Math.max(8, this.cell * 0.16);
    const c = this.scene.add.container(0, 0).setDepth(2);
    const ring = this.scene.add.circle(0, 0, r + 2, PALETTE.bg, 0.85);
    const body = this.scene.add.circle(0, 0, r, PALETTE.player);
    const mouth = this.scene.add.triangle(0, 0, 0, 0, r * 1.1, -r * 0.55, r * 1.1, r * 0.55, PALETTE.bg);
    const tag = this.scene.add
      .text(0, r * 0.42, 'YOU', {
        fontFamily: FONT.family,
        fontSize: `${Math.max(9, r * 0.5)}px`,
        color: hex(PALETTE.white),
        fontStyle: 'bold',
        stroke: hex(PALETTE.bg),
        strokeThickness: 2,
      })
      .setOrigin(0.5);
    c.add([ring, body, mouth, tag]);
    const start = this.muncherPoint(START_POS);
    c.setPosition(start.x, start.y);
    this.layer.add(c);
    this.muncher = c;
  }

  private buildTextCard(W: number, H: number): void {
    const cardY = H * 0.79;
    const cardW = W * 0.92;
    const cardH = Math.max(112, H * 0.18);
    const card = this.scene.add.rectangle(W / 2, cardY, cardW, cardH, PALETTE.panel, 0.98).setStrokeStyle(2, PALETTE.panelBorder);
    this.titleText = this.scene.add
      .text(W / 2, cardY - cardH * 0.32, '', {
        fontFamily: FONT.display,
        fontSize: `${typePx(this.scene, 'title', { vw: 0.043, min: 17, max: 32 })}px`,
        // S3 item 31: a neutral heading color — `correct` green is a gameplay signal (a right answer),
        // not title chrome. The one green element stays the primary CTA below.
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.bodyText = this.scene.add
      .text(W / 2, cardY - cardH * 0.02, '', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this.scene, 'body', { vw: 0.03, min: 13, max: 23 })}px`,
        color: hex(PALETTE.text),
        align: 'center',
        wordWrap: { width: cardW - 34 },
        lineSpacing: 2,
      })
      .setOrigin(0.5);
    this.ctaBg = this.scene.add
      .rectangle(W / 2, cardY + cardH * 0.34, cardW - 34, 30, PALETTE.correct, 0.95)
      .setStrokeStyle(2, 0x1f9e68);
    this.hintText = this.scene.add
      .text(W / 2, cardY + cardH * 0.34, 'Tap to continue ▸', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this.scene, 'body', { vw: 0.026, min: 12 })}px`,
        color: hex(PALETTE.bg),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.layer.add([card, this.titleText, this.bodyText, this.ctaBg, this.hintText]);

    // progress dots above the card
    const dotN = 8; // matches beats.length
    const dotGap = 16;
    const dotY = cardY - cardH / 2 - 14;
    const startX = W / 2 - ((dotN - 1) * dotGap) / 2;
    for (let i = 0; i < dotN; i++) {
      const d = this.scene.add.circle(startX + i * dotGap, dotY, 4, PALETTE.textDim, 0.5);
      this.progressDots.push(d);
      this.layer.add(d);
    }
  }

  private buildSkipButton(W: number, H: number): void {
    // Inline on the QUICK TUTORIAL row (right side), NOT floating in the top-right corner where it
    // crowds the RULE pill — matches the reference.
    const bannerH = Math.min(56, H * 0.075);
    const x = W - W * 0.15;
    const y = H * 0.07 + bannerH / 2 + 14;
    const pillW = W * 0.24;
    const pillH = 30;
    // S3 item 30: a clearly-visible SKIP affordance — a stronger surface fill + lineStrong border and a
    // legible `dim` label (was a faint panelBorder pill with textDim text). Tap target stays ≥44px below.
    const bg = this.scene.add
      .rectangle(x, y, pillW, pillH, PALETTE.surface, 1)
      .setStrokeStyle(1.5, PALETTE.lineStrong);
    const txt = this.scene.add
      .text(x, y, 'SKIP', {
        fontFamily: FONT.display,
        fontSize: `${typePx(this.scene, 'body', { vw: 0.024, min: 12 })}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    // SKIP + a small ✕ close glyph, grouped and centered as a unit inside the pill (matches the ref,
    // which pairs the label with a close mark rather than the word alone).
    const xs = 11;
    const xgap = 6;
    const groupW = txt.width + xgap + xs;
    txt.setX(x - groupW / 2);
    const xg = this.scene.add.graphics();
    const paintX = (c: number): void => {
      xg.clear();
      drawIcon(xg, 'x', xs, c);
    };
    paintX(PALETTE.textDim);
    xg.setPosition(x - groupW / 2 + txt.width + xgap + xs / 2, y);
    // The visual pill stays compact (inline design intent), but the tap target is an invisible
    // padded rect >=44px in BOTH dimensions (the kit.ts Math.max(...,44) pattern) so it clears the
    // repo touch minimum on phones.
    const hit = this.scene.add
      .rectangle(x, y, Math.max(pillW, 44), Math.max(pillH, 44), 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => {
      txt.setColor(hex(PALETTE.text));
      paintX(PALETTE.text);
    });
    hit.on('pointerout', () => {
      txt.setColor(hex(PALETTE.textDim));
      paintX(PALETTE.textDim);
    });
    // Skip swallows the tap so it doesn't also advance a beat — but ONLY within this padded rect;
    // taps outside it still fall through to the veil's advance handler as before.
    hit.on('pointerup', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
      ev?.stopPropagation?.();
      this.finish(true);
    });
    this.layer.add([bg, txt, xg, hit]);
  }

  // --- helpers --------------------------------------------------------------
  private cellCenter(id: number): { x: number; y: number } {
    const col = id % COLS;
    const row = Math.floor(id / COLS);
    return {
      x: this.gridX + col * this.cell + this.cell / 2,
      y: this.gridY + row * this.cell + this.cell / 2,
    };
  }

  private muncherPoint(id: number): { x: number; y: number } {
    const c = this.cellCenter(id);
    return {
      x: c.x - this.cell * 0.3,
      y: c.y + this.cell * 0.3,
    };
  }

  /** Glow-highlight a cell briefly to draw the eye (used for "read this cell" beats). */
  private highlight(id: number, color: number = PALETTE.scan): void {
    const c = this.cells[id];
    if (c) glowPulse(this.scene, c.container, this.cell - 6, this.cell - 6, color, 700);
  }

  /** Slide the muncher to a cell (snaps under reduced-motion), resolving after it arrives. */
  private moveTo(id: number, onArrive: () => void): void {
    const c = this.muncherPoint(id);
    this.pos = id;
    if (!motionAllowed()) {
      this.muncher.setPosition(c.x, c.y);
      onArrive();
      return;
    }
    this.scene.tweens.add({
      targets: this.muncher,
      x: c.x,
      y: c.y,
      duration: 220,
      ease: 'Quad.easeInOut',
      onComplete: onArrive,
    });
  }

  private munchCell(id: number, value: number, threaded = false): void {
    const c = this.cells[id];
    if (!c || c.eaten) return;
    c.eaten = true;
    c.bg.setFillStyle(PALETTE.correct, 0.22).setStrokeStyle(2, PALETTE.correct, 0.85);
    c.label.setText('');
    c.glyph.setText('✓').setColor(hex(PALETTE.correct)).setVisible(true);
    const at = this.cellCenter(id);
    particleBurst(this.scene, at.x, at.y, threaded ? PALETTE.armed : PALETTE.correct, threaded ? 16 : 12);
    floatText(this.scene, at.x, at.y, threaded ? `THREADED +${value}` : `+${value}`, hex(threaded ? PALETTE.armed : PALETTE.correct), threaded ? 20 : 18);
    // a quick bounce on the cell (returns to rest — the eaten ✓ tile persists, unlike juice.popTween
    // which scales the target to 0 for a destroy-pop).
    if (motionAllowed()) {
      this.scene.tweens.add({ targets: c.container, scale: { from: 1, to: 1.3 }, duration: 90, yoyo: true, ease: 'Quad.easeOut' });
    }
    if (threaded) glowPulse(this.scene, c.container, this.cell - 6, this.cell - 6, PALETTE.armed, 600);
    audio().playSfx(threaded ? 'gauntlet' : 'munch');
    haptic(threaded ? [20, 30, 40] : 18);
  }

  /** Show a decoy being skipped by moving away: it locks shut (dashed border + lock), never eaten. */
  private armCell(id: number): void {
    const c = this.cells[id];
    if (!c || c.eaten || c.armed) return;
    c.armed = true;
    c.bg.setFillStyle(PALETTE.lockFill, 1).setStrokeStyle(0, PALETTE.lockBorder, 0);
    drawDashedRect(c.lockBorder, this.cell - 6, this.cell - 6, PALETTE.lockBorder, { dash: 6, gap: 4, thickness: 2 });
    c.lockBorder.setVisible(true);
    // Geometric lock glyph (matches the in-game armed cell — GameScene drawIcon 'lock'); never a 🔒 emoji.
    c.glyph.setText('').setVisible(false);
    // S3 item 29: draw the lock in the tile's top-right CORNER (not dead-center over the word) so the
    // decoy word stays readable ("sock", not "so🔒ck"). translateCanvas offsets only this drawIcon
    // (issued after the centered dashed border); the graphic is baked at draw time, then we reset.
    {
      const lockS = this.cell * 0.3;
      const off = this.cell / 2 - 3 - lockS / 2 - 1;
      c.lockBorder.translateCanvas(off, -off);
      drawIcon(c.lockBorder, 'lock', lockS, PALETTE.lockGlyph);
      c.lockBorder.translateCanvas(-off, off);
    }
    c.label.setColor(hex(PALETTE.textDim)).setAlpha(0.5);
    const at = this.cellCenter(id);
    glowPulse(this.scene, c.container, this.cell - 6, this.cell - 6, PALETTE.armed, 420);
    floatText(this.scene, at.x, at.y, 'SKIPPED IT', hex(PALETTE.armed), 15);
    audio().playSfx('lock', { volume: 0.8 });
    haptic(15);
  }

  /** Reveal a cell's true status (scan): green ✓ for correct, red ✗ for decoy, with a 🔍 ring. */
  private revealCell(id: number): void {
    const c = this.cells[id];
    if (!c || c.eaten || c.armed) return;
    c.revealed = true;
    const correct = MINI[id].kind === 'correct';
    const col = correct ? PALETTE.correct : PALETTE.hazard;
    c.bg.setStrokeStyle(3, col, 0.95);
    c.glyph.setText(correct ? '✓' : '✗').setColor(hex(col)).setVisible(true);
    glowPulse(this.scene, c.container, this.cell - 6, this.cell - 6, PALETTE.scan, 520);
  }

  // --- beats ----------------------------------------------------------------
  private buildBeats(): void {
    this.beats = [
      {
        title: 'Read the rule',
        body: 'Every board has ONE rule up top. Here: eat only FRUIT.',
        run: () => {
          this.highlight(this.pos, PALETTE.player);
        },
      },
      {
        title: 'Move',
        body: 'Tap any tile to walk there (or swipe). Moving only moves; CHOMP commits an answer.',
        run: () => {
          // demo a step right and back so the player sees movement.
          this.busy = true;
          this.moveTo(this.pos + 1, () => {
            this.moveTo(START_POS, () => {
              this.busy = false;
            });
          });
        },
      },
      {
        title: 'Judge',
        body: "You're on “mango”. Ask: does it fit the rule? A mango is fruit — safe to eat.",
        run: () => {
          this.highlight(START_POS, PALETTE.correct);
        },
      },
      {
        title: 'Chomp',
        body: 'Already standing on an answer? Use CHOMP only when you want to commit this tile.',
        run: () => {
          this.busy = true;
          this.scene.time.delayedCall(motionAllowed() ? 360 : 0, () => {
            this.munchCell(START_POS, 100);
            this.busy = false;
          });
        },
      },
      {
        title: 'Skip wrong ones',
        // S3 item 26: copy MUST match the fruit board on screen — cell 4 (the demo decoy) is “sock”,
        // not a number. The old “5 is odd” line was an orphan from a different (odd/even) board.
        body: '“sock” isn’t fruit. Stand on it if you must, then skip it by moving away — it locks behind you.',
        run: () => {
          this.busy = true;
          // step right onto the decoy “sock” (cell 4, the right neighbour of start) to demo the lockout,
          // then step back off it. Cell 4 is a genuine decoy in MINI.
          this.highlight(4, PALETTE.hazard);
          this.moveTo(4, () => {
            this.armCell(4);
            vignettePulse(this.scene, PALETTE.hazard, 360, 0.22);
            this.scene.time.delayedCall(motionAllowed() ? 460 : 0, () => {
              this.moveTo(START_POS, () => {
                this.busy = false;
              });
            });
          });
        },
      },
      {
        title: 'Scan when unsure',
        body: 'Stuck? SCAN reveals the true answer on nearby cells (✓ or ✗) — but you only get a few.',
        run: () => {
          this.busy = true;
          // Reveal the bottom-lane Gauntlet decision: two wrong cells to pass, then the payoff correct.
          this.revealCell(6);
          this.scene.time.delayedCall(motionAllowed() ? 150 : 0, () => this.revealCell(7));
          this.scene.time.delayedCall(motionAllowed() ? 300 : 0, () => {
            this.revealCell(8);
            audio().playSfx('scan');
            this.busy = false;
          });
        },
      },
      {
        title: 'Gauntlet bonus',
        body: 'Thread through wrong cells to reach the right answer. Safe passes set up bigger combo bonuses.',
        run: () => {
          this.busy = true;
          // Thread the bottom lane: pass chair -> pass soap -> eat plum as the payoff.
          this.moveTo(6, () => {
            this.armCell(6);
            this.scene.time.delayedCall(motionAllowed() ? 200 : 0, () => {
              this.moveTo(7, () => {
                this.armCell(7);
                this.scene.time.delayedCall(motionAllowed() ? 200 : 0, () => {
                  this.moveTo(8, () => {
                    this.munchCell(8, 300, true); // threaded finish = the × bonus
                    this.busy = false;
                  });
                });
              });
            });
          });
        },
      },
      {
        title: "You've got it!",
        body: 'Move • CHOMP correct • Skip decoys • Scan • Thread risky paths. Now go eat some trivia!',
        run: () => {
          this.busy = true;
          particleBurst(this.scene, this.scene.scale.width / 2, this.scene.scale.height * 0.4, PALETTE.correct, 20);
          this.scene.time.delayedCall(motionAllowed() ? 300 : 0, () => {
            this.busy = false;
          });
        },
      },
    ];
  }

  private showBeat(i: number): void {
    this.beatIndex = i;
    const beat = this.beats[i];
    if (!beat) {
      this.finish(false);
      return;
    }
    this.titleText.setText(beat.title);
    this.bodyText.setText(beat.body);
    const last = i === this.beats.length - 1;
    this.hintText.setText(last ? 'Tap to play ▸' : 'Tap to continue ▸');
    this.ctaBg.setFillStyle(last ? PALETTE.armed : PALETTE.correct, 0.95);
    this.ctaBg.setStrokeStyle(2, last ? 0xb7791f : 0x1f9e68);
    // progress dots
    this.progressDots.forEach((d, k) => {
      d.setFillStyle(k <= i ? PALETTE.correct : PALETTE.textDim, k <= i ? 1 : 0.45);
    });
    // little entrance pulse on the text card hint so "tap" reads as live.
    if (motionAllowed()) {
      this.scene.tweens.killTweensOf(this.hintText);
      this.hintText.setAlpha(1);
      this.scene.tweens.add({ targets: this.hintText, alpha: 0.5, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    beat.run();
  }

  private onTap(): void {
    if (this.done) return;
    if (this.busy) return; // let the current scripted demo finish before advancing
    audio().playSfx('uiTap', { volume: 0.6 });
    track('tutorial_step_completed', { result: `step_${this.beatIndex + 1}`, boardIndex: this.beatIndex });
    const next = this.beatIndex + 1;
    if (next >= this.beats.length) {
      this.finish(false);
      return;
    }
    this.showBeat(next);
  }
}
